import { adminAPI } from 'api/AdminAPI'
import { assignmentAPI } from 'api/AssignmentAPI'
import { organizationAPI } from 'api/OrganizationAPI'
import { skillAPI } from 'api/SkillAPI'
import { teamAPI } from 'api/TeamAPI'
import { CountriesData, LanguagesData, utilsAPI } from 'api/UtilsAPI'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IEmployee } from 'types/adminInterfaces'
import { IAssignment } from 'types/assignmentInterfaces'
import { IEmployer, IProject } from 'types/cvsInterfaces'
import { ISkill } from 'types/levelItemInterfaces'
import { IError } from 'types/error'
import { SiteId } from 'types/ids'
import { ITeam } from 'types/teamInterfaces'
import { IPerson } from 'types/userInterfaces'

/**
 * Hook for getting countries data list.
 *
 * @returns Hook variables and functions.
 */
export const useCountryData = () => {
  const [countries, setCountries] = useState<CountriesData>({})
  const [error, setError] = useState<IError>()

  const { i18n } = useTranslation()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        const results = await utilsAPI.getCountriesData(i18n.language, controller)
        setCountries(results)
      } catch (error) {
        setError(error as IError)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [i18n.language])

  return {
    countries,
    error,
  }
}

/**
 * Hook for getting languages data list.
 *
 * @returns Hook variables and functions.
 */
export const useLanguageData = () => {
  const [languages, setLanguages] = useState<LanguagesData>()
  const [error, setError] = useState<IError>()

  const { i18n } = useTranslation()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        const results = await utilsAPI.getLanguagesData(i18n.language, controller)
        setLanguages(results)
      } catch (error) {
        setError(error as IError)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [i18n.language])

  return {
    languages,
    error,
  }
}

/**
 * Hook for getting skills data.
 *
 * @returns Hook variables and functions.
 */
export const useSkillsData = () => {
  const [skills, setSkills] = useState<ISkill[]>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        const results = await skillAPI.getAllSkills(controller)
        setSkills(results)
      } catch (err) {
        setError(err as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [])

  return {
    skills,
    error,
    loading,
  }
}

/**
 * Hook for getting projects or employers based on skill.
 *
 * @param id - Skill ID
 * @param type - Type of skill
 * @return Hook variables and functions.
 */
export const useConnectedSkillData = (id: number, type: string) => {
  const [projects, setProjects] = useState<IProject[]>([])
  const [employers, setEmployers] = useState<IEmployer[]>([])
  const [error, setError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        if (type === 'skill') {
          const results = await skillAPI.getSkillProjects(id, controller)
          setProjects(results)
        } else if (type === 'industry') {
          const results = await skillAPI.getIndustryProjects(id, controller)
          setProjects(results)
        } else if (type === 'role') {
          const results = await skillAPI.getRoleProjects(id, controller)
          const employerResults = await skillAPI.getRoleEmployers(id, controller)
          {
            setProjects(results)
            setEmployers(employerResults)
          }
        }
      } catch (err) {
        setError(err as IError)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [type, id])

  return {
    projects,
    employers,
    error,
  }
}

/**
 * Hook for getting people based on site ID.
 *
 * @param id - Site ID.
 * @returns Hook variables and functions.
 */
export const useConnectedSiteData = (id: SiteId) => {
  const [people, setPeople] = useState<IPerson[]>([])
  const [error, setError] = useState<IError>()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        if (id !== 0) {
          const results = await organizationAPI.getSitePeople(id, controller)
          setPeople(results)
        }
      } catch (err) {
        setError(err as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [id])

  return {
    people,
    error,
    loading,
  }
}

/**
 * Hook for getting employees, assignments and teams based on organization ID.
 *
 * @param organizationId - Organization ID
 * @param networkManage - Is user network manager
 * @returns Hook variables and functions.
 */
export const useConnectedOrganizationData = (organizationId: number, networkManage?: boolean) => {
  const [employees, setEmployees] = useState<IEmployee[]>([])
  const [assignments, setAssignments] = useState<IAssignment[]>([])
  const [teams, setTeams] = useState<ITeam[]>([])
  const [error, setError] = useState<IError>()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        if (!networkManage) {
          const employees = await adminAPI.getOrgEmployees(organizationId, controller)
          const assignments = await assignmentAPI.getOrganizationAssignments(organizationId, controller)
          const teams = await teamAPI.getOrganizationTeams(organizationId, controller)
          {
            setEmployees(employees)
            setAssignments(assignments)
            setTeams(teams)
          }
        }
      } catch (err) {
        setError(err as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [networkManage, organizationId])

  return {
    employees,
    assignments,
    teams,
    error,
    loading,
  }
}

/**
 * Function for getting value from local storage.
 *
 * @param key - Key of the local storage item.
 * @param defaultValue - Default value of the local storage item.
 * @returns Value of the local storage item.
 */
export function getStorageValue<T>(key: string, defaultValue: T): T {
  const saved = localStorage.getItem(key)
  let initial: T = defaultValue
  if (saved) {
    initial = JSON.parse(saved)
  }
  return initial || defaultValue
}

/**
 * Hook for getting and setting value of local storage item.
 *
 * @param key - Key of the local storage item.
 * @param defaultValue - Default value of the local storage item.
 * @returns Value of and function for setting local storage value.
 */
export const useLocalStorage = <T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => {
    return getStorageValue(key, defaultValue)
  })

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value))
  }, [key, value])

  return [value, setValue]
}
